/**
 * Review N1 — recipient-timezone stamp (push contract §3.10/T1).
 *
 * The push dispatcher evaluates quiet hours RECIPIENT-local and FAILS CLOSED
 * when the recipient's `_timezone` context key is unresolved — it HOLDs the
 * candidate until the tz resolves or the TTL expires. The only writer of
 * `_timezone` was the NP-1 stamp inside `useProactiveChecks` (mounted on
 * /today), which guardian-role users plausibly never visit — leaving every
 * §2.3 guardian-approval push held to death against its 72h TTL.
 *
 * This hook is that stamp EXTRACTED to its minimal form — the same value
 * (`Intl.DateTimeFormat().resolvedOptions().timeZone`) through the same
 * write path (`useAthleteContext().mergeContext`, the server-authoritative
 * context-merge endpoint) — with NONE of useProactiveChecks' athlete-side
 * behavior: no detectors, no notification writes, no debounce keys, no
 * `_last_dashboard_visit`. It writes ONLY `_timezone`, and only when the
 * resolved zone differs from the stored one (a no-op write costs a merge
 * round-trip on every visit otherwise).
 *
 * Mounted on the guardian home (guardian-home.tsx) so guardian accounts
 * stamp their tz exactly like athletes do. Safe for ANY signed-in role:
 * the stamp touches the caller's OWN context row only. Pre-existing
 * tz-less guardians heal on their next /guardian visit.
 *
 * Guards: skips while the context is loading, and after a FAILED load
 * (`loadFailed` — empty-because-unknown must never be patched as if it
 * were truth); one attempt per mount.
 *
 * B299 fix1 (security F5) — AND NEVER FOR A KNOWN UNDER-13 ACCOUNT. The age
 * floor (`api/lib/account/ageFloor.js`) refuses every context write that
 * touches a key outside its three-key allowlist for an account whose age is
 * 12 or under, and `_timezone` is not on that list. The provider batches
 * writes inside 300 ms and folds a newer write into a retried batch for up to
 * 20 s, so a stamp sharing a batch with an allowlisted write (withdrawing
 * result sharing, say) got the WHOLE batch refused and dropped while the toggle
 * kept showing the new value. Such an account is skipped here, so its stamp
 * never rides in any batch. The age is read exactly as the floor reads it
 * (below); an account whose age is unknown is stamped as before, since the
 * floor refuses only a known 12 or under.
 */

import { useEffect, useRef } from 'react';
import { UNDER_13_MAX_AGE } from '../lib/onboardingAgeCopy';
import { useAthleteContext } from './useAthleteContext';

/**
 * The age the server's floor would establish from this row: the birth pair
 * first (the birthday month counts as not yet passed, so it errs low), then
 * `age`, then `Age`, with NO lower clamp — a stated 11 reads as 11, and no age
 * at all reads as null. A port of `establishedAgeUnclamped`
 * (api/lib/account/effectiveAge.js), NOT of the [5,110]-clamped
 * `ageFromContext`, which would read a stated 4 as unknown.
 */
export function establishedAgeFromContext(
  ctx: Record<string, unknown>,
  now: Date = new Date(),
): number | null {
  if (ctx._birth_year != null && ctx._birth_month != null) {
    const y = parseInt(String(ctx._birth_year).replace(/[^\d]/g, ''), 10);
    const m = parseInt(String(ctx._birth_month).replace(/[^\d]/g, ''), 10);
    if (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12) {
      let age = now.getFullYear() - y;
      if (now.getMonth() + 1 <= m) age -= 1;
      return age;
    }
  }
  const stated = ctx.age != null ? ctx.age : ctx.Age;
  const n = parseInt(String(stated == null ? '' : stated).replace(/[^\d]/g, ''), 10);
  return Number.isFinite(n) ? n : null;
}

export function useTimezoneStamp(): void {
  const { raw, loading, loadFailed, mergeContext } = useAthleteContext();
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current || loading || loadFailed) return;
    let tz: string | null = null;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      // best effort — an unresolvable browser tz stays unstamped (the
      // dispatcher's fail-closed hold is the correct posture then)
    }
    if (!tz) return;
    doneRef.current = true; // one attempt per mount, even when equal
    if (raw._timezone === tz) return;
    // A known 12 or under is refused by the age floor (and would take a shared
    // batch down with it): no write at all, so no refusal to drop.
    const age = establishedAgeFromContext(raw);
    if (age != null && age <= UNDER_13_MAX_AGE) return;
    mergeContext({ _timezone: tz });
  }, [loading, loadFailed, raw, mergeContext]);
}
